import { FC } from 'react';
import { Star, Check, UserCircle } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Avatar } from '../../../components/ui/Avatar';
import { Spinner } from '../../../components/ui/Spinner';
import { cn } from '../../../utils/cn';
import { useTherapistsForService } from '../../../hooks/useTherapists';
import { useBooking } from '../../../contexts/BookingContext';
import { TherapistForService } from '../../../types/booking.types';

export const Step2TherapistSelection: FC = () => {
  const { formData, updateFormData, nextStep } = useBooking();
  const { data: therapists, isLoading, error } = useTherapistsForService(formData.serviceId);

  const handleTherapistSelect = (therapistId: string | null) => {
    updateFormData({ therapistId });
    setTimeout(() => {
      nextStep();
    }, 150);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" label="Loading therapists..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <p className="text-red-700 dark:text-red-400">Failed to load therapists. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Choose your therapist</h2>
        <p className="mt-1 text-gray-600 dark:text-slate-400">
          Select a therapist or let us match you with the best available.
        </p>
      </div>

      <div className="grid gap-4">
        {/* No Preference Option */}
        <NoPreferenceCard
          isSelected={formData.therapistId === null}
          onSelect={() => handleTherapistSelect(null)}
        />

        {/* Therapist Cards */}
        {therapists?.map((therapist) => (
          <TherapistCard
            key={therapist.id}
            therapist={therapist}
            isSelected={formData.therapistId === therapist.id}
            onSelect={() => handleTherapistSelect(therapist.id)}
          />
        ))}
      </div>
    </div>
  );
};

interface NoPreferenceCardProps {
  isSelected: boolean;
  onSelect: () => void;
}

const NoPreferenceCard: FC<NoPreferenceCardProps> = ({ isSelected, onSelect }) => {
  return (
    <Card
      variant="outlined"
      padding="none"
      hoverable
      clickable
      onClick={onSelect}
      className={cn(
        'cursor-pointer transition-all',
        isSelected && 'ring-2 ring-primary-500 border-primary-500'
      )}
      role="button"
      aria-pressed={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700">
            <UserCircle className="h-8 w-8 text-gray-400 dark:text-slate-500" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">No Preference</h3>
              {isSelected && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
                  <Check className="h-3 w-3 text-white" aria-hidden="true" />
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              We&apos;ll match you with the best available therapist for your chosen service and
              time.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

interface TherapistCardProps {
  therapist: TherapistForService;
  isSelected: boolean;
  onSelect: () => void;
}

const TherapistCard: FC<TherapistCardProps> = ({ therapist, isSelected, onSelect }) => {
  return (
    <Card
      variant="outlined"
      padding="none"
      hoverable
      clickable
      onClick={onSelect}
      className={cn(
        'cursor-pointer transition-all',
        isSelected && 'ring-2 ring-primary-500 border-primary-500'
      )}
      role="button"
      aria-pressed={isSelected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          <Avatar
            name={therapist.name}
            src={therapist.photoUrl || undefined}
            size="lg"
            shape="circle"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">{therapist.name}</h3>
              {isSelected && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
                  <Check className="h-3 w-3 text-white" aria-hidden="true" />
                </span>
              )}
            </div>

            {/* Rating */}
            {therapist.rating !== null && (
              <div className="mt-1 flex items-center gap-1">
                <Star className="h-4 w-4 text-amber-400 fill-current" aria-hidden="true" />
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                  {therapist.rating.toFixed(1)}
                </span>
                <span className="text-sm text-gray-500 dark:text-slate-400">({therapist.totalReviews} reviews)</span>
              </div>
            )}

            {/* Bio */}
            {therapist.bio && (
              <p className="mt-2 text-sm text-gray-600 dark:text-slate-400 line-clamp-2">{therapist.bio}</p>
            )}

            {/* Specializations */}
            {therapist.specializations.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {therapist.specializations.slice(0, 3).map((spec) => (
                  <Badge key={spec} variant="secondary" size="sm">
                    {spec}
                  </Badge>
                ))}
                {therapist.specializations.length > 3 && (
                  <Badge variant="secondary" size="sm">
                    +{therapist.specializations.length - 3} more
                  </Badge>
                )}
              </div>
            )}

            {/* Experience */}
            {therapist.yearsOfExperience > 0 && (
              <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
                {therapist.yearsOfExperience} years experience
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
